import React, { memo, useState, useCallback, useMemo, useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import styles from './styles.module.scss'
import { useFlowerSelection } from './hooks/useFlowerSelection'
import { PATH_URL } from 'src/helpers/path'
import { getQueryParameters } from 'src/helpers/tool'
import { preserveUrlParams } from 'src/helpers/preserveUrlParams'
import FlowerHeader from './components/FlowerHeader'
import FlowerGrid from './components/FlowerGrid'
import ProgressBar from '../ProgressBar'

/**
 * FlowerSelectionPage Component
 * Allows users to select their preferred flowers for their arrangement
 * @returns {JSX.Element} The FlowerSelectionPage component
 */
const FlowerSelectionPage: React.FC = () => {
  const history = useHistory()
  const { images, error } = useFlowerSelection()
  const [selectedFlowers, setSelectedFlowers] = useState<Set<number>>(new Set())
  const [isSaving, setIsSaving] = useState(false)
  const MIN_SELECTED_FLOWERS = 3

  // Check for collectionId parameter and redirect if present
  useEffect(() => {
    const params = getQueryParameters()
    const collectionId = params.get('collectionId')

    if (collectionId) {
      console.log('Collection ID detected in FlowerSelectionPage, redirecting to EmailEntryPage')
      // Navigate preserving all URL parameters
      const nextUrl = preserveUrlParams(PATH_URL.COLLECTIONS_QUIZ_EMAIL_ENTRY)
      history.replace(nextUrl)
    }
  }, [history])

  const selectedImages = useMemo(() => Array.from(selectedFlowers).map((idx) => images[idx]), [selectedFlowers, images])

  const selectedImageIds = useMemo(() => selectedImages.map((img) => img.id), [selectedImages])

  const handleFlowerSelect = useCallback((index: number) => {
    setSelectedFlowers((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(index)) {
        newSet.delete(index)
      } else {
        newSet.add(index)
      }
      return newSet
    })
  }, [])

  const canContinue = selectedFlowers.size >= MIN_SELECTED_FLOWERS
  const handleContinue = useCallback(async () => {
    if (selectedFlowers.size >= MIN_SELECTED_FLOWERS) {
      try {
        setIsSaving(true)
        if (!selectedImages.length) {
          throw new Error('No flowers selected')
        }
        // save collectionsQuizEntry when user click next button
        const collectionsQuizEntry = JSON.parse(localStorage.getItem('collectionsQuizEntry') || '{}')
        collectionsQuizEntry.flowerSelections = selectedImages
        localStorage.setItem('collectionsQuizEntry', JSON.stringify(collectionsQuizEntry))
        // Save to localStorage
        localStorage.setItem('selectedFlowers', JSON.stringify(selectedImages))

        // Navigate to next page preserving URL parameters
        const nextUrl = preserveUrlParams(PATH_URL.COLLECTIONS_QUIZ_EMAIL_ENTRY)
        history.push(nextUrl)
      } catch (error) {
        console.error('Error saving flower selections:', error)
      } finally {
        setIsSaving(false)
      }
    }
  }, [selectedFlowers, selectedImages, selectedImageIds, history])

  if (error) {
    return (
      <div className={styles.error}>
        <p>{error}</p>
        <button onClick={() => window.location.reload()}>Try Again</button>
      </div>
    )
  }

  return (
    <>
      <ProgressBar />
      <section className={styles.flowerSelection} aria-labelledby="flower-selection-title">
        <FlowerHeader
          canContinue={canContinue}
          selectedCount={selectedFlowers.size}
          minRequired={MIN_SELECTED_FLOWERS}
          onContinue={handleContinue}
          isSaving={isSaving}
        />
        <FlowerGrid flowers={images} selectedFlowers={selectedFlowers} onFlowerSelect={handleFlowerSelect} />
      </section>
    </>
  )
}

FlowerSelectionPage.displayName = 'FlowerSelectionPage'

export default memo(FlowerSelectionPage)
